import { ExtractedFrame, PackedFrame, PackedTexture } from './types.ts';

// ---------------------------------------------------------------------------
// Shelf-bin packing (ported from server atlasPacker.ts — logic is identical)
// ---------------------------------------------------------------------------

class Shelf {
  y: number;
  height: number;
  maxWidth: number;
  currentX: number;
  frames: PackedFrame[];

  constructor(y: number, height: number, maxWidth: number) {
    this.y = y;
    this.height = height;
    this.maxWidth = maxWidth;
    this.currentX = 0;
    this.frames = [];
  }

  canFit(paddedWidth: number, paddedHeight: number): boolean {
    return this.currentX + paddedWidth <= this.maxWidth && paddedHeight <= this.height;
  }

  add(frame: ExtractedFrame, padding: number): PackedFrame {
    const packed: PackedFrame = {
      frame,
      x: this.currentX + padding,
      y: this.y + padding,
    };
    this.frames.push(packed);
    this.currentX += frame.width + padding * 2;
    return packed;
  }

  getUsedWidth(): number {
    return this.currentX;
  }
}

class Texture {
  maxWidth: number;
  maxHeight: number;
  padding: number;
  shelves: Shelf[];
  currentY: number;

  constructor(maxWidth: number, maxHeight: number, padding: number) {
    this.maxWidth = maxWidth;
    this.maxHeight = maxHeight;
    this.padding = padding;
    this.shelves = [];
    this.currentY = 0;
  }

  tryAdd(frame: ExtractedFrame): PackedFrame | null {
    const paddedWidth = frame.width + this.padding * 2;
    const paddedHeight = frame.height + this.padding * 2;

    for (const shelf of this.shelves) {
      if (shelf.canFit(paddedWidth, paddedHeight)) {
        return shelf.add(frame, this.padding);
      }
    }

    if (this.currentY + paddedHeight <= this.maxHeight) {
      const shelf = new Shelf(this.currentY, paddedHeight, this.maxWidth);
      this.shelves.push(shelf);
      this.currentY += paddedHeight;
      return shelf.add(frame, this.padding);
    }

    return null;
  }

  getFrames(): PackedFrame[] {
    return this.shelves.flatMap(s => s.frames);
  }

  getUsedDimensions(): { width: number; height: number } {
    let maxX = 0;
    let maxY = 0;
    for (const shelf of this.shelves) {
      maxX = Math.max(maxX, shelf.getUsedWidth());
      maxY = Math.max(maxY, shelf.y + shelf.height);
    }
    return { width: maxX, height: maxY };
  }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Pack extracted frames into one or more atlas textures using shelf-bin packing.
 * Frames are sorted tallest-first for better packing density.
 */
export function pack(
  frames: ExtractedFrame[],
  maxSize: number,
  padding = 1,
): PackedTexture[] {
  if (frames.length === 0) return [];

  const sorted = [...frames].sort((a, b) => {
    if (b.height !== a.height) return b.height - a.height;
    return b.width - a.width;
  });

  const textures: Texture[] = [new Texture(maxSize, maxSize, padding)];

  for (const frame of sorted) {
    const paddedW = frame.width + padding * 2;
    const paddedH = frame.height + padding * 2;

    if (paddedW > maxSize || paddedH > maxSize) {
      console.warn(`[AtlasPacker] Frame "${frame.name}" (${frame.width}×${frame.height}) exceeds max texture size — skipped.`);
      continue;
    }

    let packed = textures[textures.length - 1].tryAdd(frame);
    if (!packed) {
      const next = new Texture(maxSize, maxSize, padding);
      textures.push(next);
      packed = next.tryAdd(frame);
      if (!packed) {
        console.error(`[AtlasPacker] Failed to pack frame "${frame.name}" even in a fresh texture.`);
      }
    }
  }

  return textures
    .map(t => {
      const dims = t.getUsedDimensions();
      return { width: dims.width, height: dims.height, frames: t.getFrames() };
    })
    .filter(t => t.frames.length > 0);
}
